import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { pickCalendarFilters, pickCalendarBootstrap } from '../../calendar-common/viewStoreKeys.js';
import { motion, AnimatePresence } from 'framer-motion';
import {
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Chip,
	Divider,
	Spinner,
	Table,
	TableHeader,
	TableColumn,
	TableBody,
	TableRow,
	TableCell,
	Breadcrumbs,
	BreadcrumbItem,
	Input,
	addToast
} from '@heroui/react';
import { ChevronLeft, ChevronDown, ChevronUp, CheckCircle, XCircle, Pencil } from 'lucide-react';
import ResourcesApi from '../../../api/resourcesApi.js';
import DiscrepanciesApi from '../../../api/discrepanciesApi.js';
import DiscrepancyAddScreen from './DiscrepancyAddScreen.js';
import DiscrepanciesSection from './DiscrepanciesSection.js';
import CounterApi from '../../../api/CounterApi.js';
import { unwrap } from '../utils/apiEnvelope.js';
import { getAirworthinessStatusFromInspections } from '../../resources/utils/inspectionStatus.js';
// Rem22 Sub 5 + Sub 11 — canonical helpers extracted to a shared util so
// CheckInScreen can copy from one source instead of inventing parallels.
import {
	DISC_STATUS_CONFIG,
	DISC_PRIORITY,
	formatDateValue
} from '../utils/discrepancyDisplay.js';
// M13 (Rem38) — the comment-required gate was dropped after the backender
// landed `a2ceb97` (NotBlank removed from `CounterRequestDto::$comments`,
// verified in git log + DTO 2026-05-29). Legacy Day-View has no comment field
// on inline counter edit. `comments` is still sent (empty allowed) because
// `CounterManager::editCounter` stores it for the audit log.
import { buildCounterSavePayload } from '../utils/buildCounterSavePayload.js';

// ─── Counter normalization ────────────────────────────────────────────────────

function normalizeCounter(c, eventState) {
	const rawStart = c.oldValue ?? c.startValue ?? c.start ?? null;
	const rawEnd = c.value ?? c.endValue ?? c.end ?? null;
	const rawTotal = c.diff ?? c.totalValue ?? c.total ?? null;

	let start = rawStart;
	let end = rawEnd;
	let total = rawTotal;

	// M11 (Rem38) — event-state-aware display mapping. Authoritative spec
	// (`project-metersection-edit-spec`) + legacy `calendar.event-resource.js`:
	//   Not Started / Checked Out → Start only      (End / Total = "—")
	//   Checked In                → Start + End + Total
	// `getEventHistory` rows carry `oldValue` + `value` + `diff`, which would
	// otherwise render as a full (start, end, total) triple even in Checked-Out
	// state — e.g. after a Pencil edit at Check Out, the pre-edit reading leaked
	// into Start and the post-edit reading into End (user screenshot
	// 2026-05-29 13.05). Collapse to Start-only unless the event is Checked In.
	const isCheckedIn = !!eventState?.hasCheckIn;
	const isCheckedOut = !!eventState?.hasCheckOut && !isCheckedIn;

	if (isCheckedOut) {
		// Checked Out (pre-flight, started): the latest reading — the post-Pencil
		// current aircraft counter (`value`/`rawEnd`) — IS the Start. End/Total
		// are meaningless until Check In. Legacy parity: Start-only display.
		start = rawEnd ?? rawStart ?? null;
		end = null;
		total = null;
	} else if (!isCheckedIn && !rawStart && !rawTotal && rawEnd != null) {
		// Not Started (or fallback aircraft counters): a current reading
		// (`value`) but no flight history (`!oldValue && !diff`) — the current
		// reading IS the start. Move `value` → start, blank end/total. Legacy
		// `calendar.event-resource.js:102-108`. Total renders "—" (not "0.00").
		start = rawEnd;
		end = null;
		total = null;
	}
	// Checked In: leave rawStart → start, rawEnd → end, rawTotal → total so the
	// backend's settled (oldValue, value, diff) triple renders both columns.

	return {
		id: c.id,
		// The aircraft-counter row id used by `CounterApi.editAircraftCounter`.
		// Aircraft counters carry it as `id`; `counter/history` rows nest it.
		counterId: c.counterId ?? c.counter?.id ?? c.id,
		name: c.name || c.counterName || c.type || String(c.id),
		start,
		end,
		total,
		// Preserved so `buildCounterSavePayload` can re-send them on save and
		// avoid the backend clobbering the persisted rate config.
		rate: c.rate ?? null,
		enableRate: c.enableRate ?? null
	};
}

function extractCounters(payload) {
	if (!payload) return [];
	if (Array.isArray(payload)) return payload.map(normalizeCounter);
	if (Array.isArray(payload.counters)) return payload.counters.map(normalizeCounter);
	if (Array.isArray(payload.data)) return payload.data.map(normalizeCounter);
	return [];
}

// ─── Show Details ─────────────────────────────────────────────────────────────

function ShowDetailsPanel({ resourceData, isAircraft }) {
	if (!resourceData) return null;
	const rows = [];
	if (isAircraft) {
		const a = resourceData.aircraft || resourceData;
		// Registration
		if (a.registrationNumber || a.regNumb)
			rows.push(['Registration Number', a.registrationNumber || a.regNumb]);
		// Pilot Rating Required (J)
		const pilotRating =
			a.pilotRatingName ||
			a.pilotRating?.name ||
			(Array.isArray(a.pilotRatings) && a.pilotRatings[0]?.name) ||
			null;
		if (pilotRating) rows.push(['Pilot Rating Required', pilotRating]);
		// Instructor Rating Required (J)
		const instrRating =
			a.instructorRatingName ||
			a.instructorRating?.name ||
			(Array.isArray(a.instructorRatings) && a.instructorRatings[0]?.name) ||
			null;
		if (instrRating) rows.push(['Instructor Rating Required', instrRating]);
		// Medical Certificate Required (J)
		const medCert = resourceData.medicalCertificate || a.medicalCertificate;
		const medCertStr =
			medCert?.name || (medCert && typeof medCert !== 'object' ? String(medCert) : null);
		if (medCertStr) rows.push(['Medical Certificate Required', medCertStr]);
		// Endorsements (J)
		const endorsements = resourceData.endorsements || a.endorsements || [];
		if (endorsements.length)
			rows.push(['Endorsements', endorsements.map(e => e.name || e).join(', ')]);
		// NOTE: "Aircraft: model" line intentionally omitted (K — user decision: full legacy parity)
	} else {
		const s = resourceData.simulator || resourceData;
		if (s.name) rows.push(['Name', s.name]);
	}
	if (!rows.length) return null;
	return (
		<div className="mt-3 rounded-lg border border-default-200 p-3 bg-default-50">
			{/* M12 (Rem38) — strict 12-col grid (col-span-5 label / col-span-7
			    value) mirrors CheckInScreen's Aircraft accordion details so labels
			    and values line up at fixed x-positions regardless of label length.
			    The prior `flex + min-w-[160px]` drifted: a label longer than 160px
			    (e.g. "Instructor Rating Required") pushed that row's value right. */}
			{rows.map(([label, value]) => (
				<div key={label} className="grid grid-cols-12 gap-2 items-center text-sm mb-1 last:mb-0">
					<span className="col-span-5 text-default-500 font-medium">{label}</span>
					<span className="col-span-7 text-default-800">{value}</span>
				</div>
			))}
		</div>
	);
}

// ─── Meters section ───────────────────────────────────────────────────────────

// M11-merge (Rem38) — `getEventHistory` only returns counters that have edit
// history (Pencil edits + CheckOut/CheckIn rows). For a Checked-Out event where
// the user Pencil-edited only one counter, history has 1 row → the other
// aircraft counters would vanish under the old all-or-nothing
// `counters.length ? counters : fallbackCounters`. Merge instead: `fallbackCounters`
// (aircraft.counters — the COMPLETE set, current value as Start) is the BASE;
// each counter that DOES have a history row is overridden with it (post-Pencil
// values / oldValue+diff for Checked-In). Match by counterId. Both inputs are
// already `normalizeCounter`-mapped, so the merge is shape-stable + key-safe.
function mergeCountersWithFallback(historyCounters, fallbackCounters) {
	const fb = Array.isArray(fallbackCounters) ? fallbackCounters : [];
	const hist = Array.isArray(historyCounters) ? historyCounters : [];
	if (!fb.length) return hist;
	if (!hist.length) return fb;
	const histByCounterId = new Map(hist.map(c => [String(c.counterId ?? c.id), c]));
	const merged = fb.map(f => histByCounterId.get(String(f.counterId ?? f.id)) ?? f);
	// Defensive: keep any history counter not present in the fallback set so an
	// edited counter is never lost if `aircraft.counters` is somehow incomplete.
	const fbIds = new Set(fb.map(f => String(f.counterId ?? f.id)));
	hist.forEach(c => {
		if (!fbIds.has(String(c.counterId ?? c.id))) merged.push(c);
	});
	return merged;
}

function MetersSection({
	counters,
	fallbackCounters,
	canEditCounter,
	orgUnique,
	eventId,
	organizationId,
	timezone,
	resourceId,
	isSimulator
}) {
	const [localRows, setLocalRows] = useState(() =>
		mergeCountersWithFallback(counters, fallbackCounters)
	);
	// editing: { id, field: 'start'|'end' } | null
	const [editing, setEditing] = useState(null);
	// edits keyed by `${id}:${field}` so Start + End drafts never collide
	const [edits, setEdits] = useState({});
	// savingKey is the same `${id}:${field}`
	const [savingKey, setSavingKey] = useState(null);

	// Re-sync when the parent refetches the counter list.
	useEffect(() => {
		setLocalRows(mergeCountersWithFallback(counters, fallbackCounters));
	}, [counters, fallbackCounters]);

	// O: 2 decimal precision
	const fmt = v => (v != null && Number.isFinite(Number(v)) ? Number(v).toFixed(2) : v ?? '—');

	const editKey = (id, field) => `${id}:${field}`;

	// Accept both `.` and `,` as decimal separators (matches backend regex
	// `/^([0-9.,]*)$/u`). Browser `<input type="number">` silently strips
	// commas in some locales — we use `type="text"` + this validator instead.
	const isValueValid = v => /^[0-9]+([.,][0-9]+)?$/.test(String(v ?? '').trim());

	// Normalize comma to dot before sending so the persisted value is canonical
	// (backend stores the raw string; consistency keeps downstream math sane).
	const normalizeDecimal = v => String(v ?? '').trim().replace(',', '.');

	const startEdit = useCallback(
		(id, field) => {
			const row = localRows.find(r => String(r.id) === String(id));
			const initial = field === 'start' ? row?.start : row?.end;
			setEdits(prev => ({
				...prev,
				[editKey(id, field)]: { value: String(initial ?? ''), comments: '' }
			}));
			setEditing({ id: String(id), field });
		},
		[localRows]
	);

	const cancelEdit = useCallback((id, field) => {
		setEditing(prev =>
			prev && String(prev.id) === String(id) && prev.field === field ? null : prev
		);
		setEdits(prev => {
			const next = { ...prev };
			delete next[editKey(id, field)];
			return next;
		});
	}, []);

	const saveEdit = useCallback(
		async (id, field) => {
			const key = editKey(id, field);
			const edit = edits[key];
			if (!edit) return;
			if (!isValueValid(edit.value)) {
				addToast({
					title: 'Invalid value',
					description: 'Enter a number (use `.` or `,` for decimal).',
					color: 'warning'
				});
				return;
			}
			const row = localRows.find(r => String(r.id) === String(id));
			if (!row) return;
			// Normalize comma → dot so the backend receives canonical decimals
			// regardless of the user's locale-driven input habit.
			const normalizedValue = normalizeDecimal(edit.value);
			// Per Brainstormer's authoritative spec (Rem37 sub-epic 5fe43b10,
			// 2026-05-27): display mapping MUST mirror the save-handler's
			// event-state branching. Without that, a pre-flight Start save
			// populates `oldValue` on the backend (via util's fallback), which
			// flips the row to post-flight after refetch — and the user's new
			// value appears in the End column instead of Start.
			//
			// `c.value` semantics for `buildCounterSavePayload`:
			//   - util's `oldValue` fallback: `payload.oldValue = c.value` when
			//     `edit.oldValue` is missing.
			//   - util's `value`    fallback: `payload.value    = c.value` when
			//     `edit.value`    is missing.
			//
			// Branch on `row.end == null` (= pre-flight signal — the same one
			// `normalizeCounter` uses to shuffle `value → start`):
			//   - PRE-FLIGHT Start edit  → send `{value: newReading}`; pass
			//     `c.value = null` to SUPPRESS the oldValue fallback. Backend
			//     stays in pre-flight (`oldValue = null`), refetch shuffle
			//     re-fires, and the new reading lands in the Start column.
			//   - POST-FLIGHT Start edit → editing the locked `oldValue` side;
			//     send `{oldValue: newStart}`. Pass `c.value = row.end` so the
			//     util's `value` fallback preserves the unchanged End reading.
			//   - POST-FLIGHT End edit   → editing the `value` side; send
			//     `{value: newEnd}`. Pass `c.value = row.start` so the util's
			//     `oldValue` fallback preserves the unchanged Start reading.
			const isPreFlight = row.end == null;
			let editShape;
			let counterForBuilder;
			if (isPreFlight) {
				editShape = { value: normalizedValue, comments: edit.comments };
				counterForBuilder = { value: null, rate: row.rate, enableRate: row.enableRate };
			} else if (field === 'start') {
				editShape = { oldValue: normalizedValue, comments: edit.comments };
				counterForBuilder = { value: row.end, rate: row.rate, enableRate: row.enableRate };
			} else {
				editShape = { value: normalizedValue, comments: edit.comments };
				counterForBuilder = { value: row.start, rate: row.rate, enableRate: row.enableRate };
			}
			const payload = buildCounterSavePayload(counterForBuilder, editShape, {
				eventId,
				organizationId,
				organizationUnique: orgUnique,
				aircraftId: isSimulator ? null : resourceId,
				isSimulatorResource: isSimulator,
				timezone
			});
			setSavingKey(key);
			try {
				if (isSimulator) {
					await CounterApi.editSimulatorCounter(resourceId, payload);
				} else {
					// Aircraft route is keyed by the counter row id.
					await CounterApi.editAircraftCounter(row.counterId ?? row.id, payload);
				}
				// Optimistic update — trust the entered value until the next
				// panel refresh; recompute Total ONLY when both sides are
				// finite numbers. `Number(null) === 0` so the previous code
				// produced `-56.12` for pre-flight rows (end=null).
				setLocalRows(prev =>
					prev.map(r => {
						if (String(r.id) !== String(id)) return r;
						const nextStart = field === 'start' ? normalizedValue : r.start;
						const nextEnd = field === 'end' ? normalizedValue : r.end;
						const startNum =
							nextStart != null && nextStart !== '' ? Number(nextStart) : null;
						const endNum =
							nextEnd != null && nextEnd !== '' ? Number(nextEnd) : null;
						const total =
							startNum != null &&
							endNum != null &&
							Number.isFinite(startNum) &&
							Number.isFinite(endNum)
								? (endNum - startNum).toFixed(2)
								: null;
						return {
							...r,
							start: nextStart,
							end: nextEnd,
							total
						};
					})
				);
				cancelEdit(id, field);
				addToast({ title: 'Meter value saved', color: 'success' });
			} catch (err) {
				const msg = err?.response?.data?.error?.[0] || err?.message || 'Save failed';
				addToast({ title: 'Meter save failed', description: msg, color: 'danger' });
			} finally {
				setSavingKey(prev => (prev === key ? null : prev));
			}
		},
		[edits, localRows, eventId, organizationId, orgUnique, isSimulator, resourceId, timezone, cancelEdit]
	);

	if (!localRows.length) {
		return <p className="text-xs text-default-400 italic">No meter data available.</p>;
	}

	// Inline editor — shared between Start and End cells.
	const renderEditor = (c, field) => {
		const key = editKey(c.id, field);
		const edit = edits[key] || {};
		const saving = savingKey === key;
		const valueValid = isValueValid(edit.value);
		const canSave = !saving && valueValid;
		return (
			<div className="flex flex-col items-end gap-1.5 py-1">
				{/* Row 1: value input + Save + Cancel inline */}
				<div className="flex items-center gap-1.5">
					<Input
						size="sm"
						/* `type="text"` (not "number") so the browser doesn't silently
						   reject comma decimals; `inputMode="decimal"` keeps the mobile
						   numeric keyboard. `isValueValid` regex accepts both `.` and
						   `,`, matching the backend's `/^([0-9.,]*)$/u`. */
						type="text"
						inputMode="decimal"
						aria-label={`${field === 'start' ? 'Start' : 'End'} meter value`}
						value={String(edit.value ?? '')}
						onValueChange={v =>
							setEdits(prev => ({
								...prev,
								[key]: { ...prev[key], value: v }
							}))
						}
						isInvalid={edit.value !== '' && edit.value != null && !valueValid}
						className="w-28"
					/>
					{/* Plain `<button>` (not HeroUI Button) — React Aria's `onPress`
					    was failing to enqueue React state updates in this tree, so
					    Cancel/Save clicks needed an unrelated parent re-render to
					    take effect. Plain `onClick` is the most direct path.
					    Inline `background` token (not Tailwind `bg-success`) so the
					    enabled state renders as solid green — `bg-success` was a
					    soft tint that read as "disabled". */}
					<button
						type="button"
						disabled={!canSave}
						onClick={() => saveEdit(c.id, field)}
						style={{
							background: canSave ? 'hsl(var(--heroui-success))' : undefined
						}}
						className={
							canSave
								? 'px-3 h-8 text-xs font-medium rounded-md text-white hover:opacity-90 transition-opacity'
								: 'px-3 h-8 text-xs font-medium rounded-md bg-default-200 text-default-500 cursor-not-allowed'
						}
					>
						{saving ? 'Saving…' : 'Save'}
					</button>
					<button
						type="button"
						onClick={() => cancelEdit(c.id, field)}
						className="px-3 h-8 text-xs font-medium rounded-md border border-default-300 text-default-700 hover:bg-default-100 transition-colors"
					>
						Cancel
					</button>
				</div>
				{/* M13 — Comment input removed (legacy Day-View has none; backend
				    NotBlank dropped in a2ceb97). `comments: ''` still flows through
				    `buildCounterSavePayload` for the audit log. */}
			</div>
		);
	};

	// Div-grid layout (mirrors the DiscrepanciesSection fix in epic
	// `a4c52470`). React Aria's HeroUI `<Table>` was swallowing the nested
	// Pencil `<Button>` onPress, so meter editing didn't trigger. Plain
	// `<div>` + click handlers behave normally.
	return (
		<div role="table" aria-label="Meter Values" className="w-full text-sm">
			<div
				role="row"
				className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1.4fr)_minmax(0,1fr)] gap-2 px-2 py-2 text-xs font-semibold text-default-500 uppercase tracking-wide border-b border-default-200"
			>
				<div role="columnheader">Name</div>
				<div role="columnheader" className="text-right">
					Start
				</div>
				<div role="columnheader" className="text-right">
					End
				</div>
				<div role="columnheader" className="text-right">
					Total
				</div>
			</div>
			{localRows.map((c, idx) => {
				const isEditingStart =
					editing && String(editing.id) === String(c.id) && editing.field === 'start';
				const isEditingEnd =
					editing && String(editing.id) === String(c.id) && editing.field === 'end';
				// Pencil matrix per Brainstormer's authoritative spec (Rem37
				// sub-epic 5fe43b10, comment 2026-05-27) — legacy parity with
				// `calendar.event-resource.js:299` (Start) + `:323-333` (End):
				//   Not Started (`end == null`): Start pencil shown, End hidden.
				//   Checked Out (`end == null`): Start pencil shown, End hidden.
				//   Checked In  (`end != null`): BOTH pencils shown.
				// Start gate = `canEditCounter` ONLY (no `c.end == null`); End
				// gate = `c.end != null` ONLY (no `canEditCounter` — legacy
				// parity, Reviewer Finding #1 intentionally rejected).
				const showStartPencil = canEditCounter;
				const showEndPencil = c.end != null;
				return (
					<div
						role="row"
						key={c.id ?? c.name}
						className={`grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1.4fr)_minmax(0,1fr)] gap-2 px-2 py-2 items-center ${
							idx % 2 === 1 ? 'bg-default-50' : ''
						}`}
					>
						<div role="cell" className="font-medium">
							{c.name}
						</div>

						{/* START */}
						<div role="cell" className="text-right tabular-nums">
							{isEditingStart ? (
								renderEditor(c, 'start')
							) : (
								<span className="inline-flex items-center justify-end gap-1">
									{fmt(c.start)}
									{showStartPencil ? (
										<button
											type="button"
											aria-label={`Edit ${c.name} start meter value`}
											onClick={() => startEdit(c.id, 'start')}
											className="inline-flex items-center justify-center w-7 h-7 rounded-md text-default-500 hover:text-foreground hover:bg-default-100 transition-colors"
										>
											<Pencil className="w-3.5 h-3.5" />
										</button>
									) : null}
								</span>
							)}
						</div>

						{/* END */}
						<div role="cell" className="text-right tabular-nums">
							{isEditingEnd ? (
								renderEditor(c, 'end')
							) : (
								<span className="inline-flex items-center justify-end gap-1">
									{fmt(c.end)}
									{showEndPencil ? (
										<button
											type="button"
											aria-label={`Edit ${c.name} end meter value`}
											onClick={() => startEdit(c.id, 'end')}
											className="inline-flex items-center justify-center w-7 h-7 rounded-md text-default-500 hover:text-foreground hover:bg-default-100 transition-colors"
										>
											<Pencil className="w-3.5 h-3.5" />
										</button>
									) : null}
								</span>
							)}
						</div>

						<div role="cell" className="text-right tabular-nums">
							{fmt(c.total)}
						</div>
					</div>
				);
			})}
		</div>
	);
}

// ─── Inspections section ──────────────────────────────────────────────────────

function InspectionsSection({ inspections, userSettings, timezone }) {
	if (!Array.isArray(inspections) || !inspections.length) {
		return <p className="text-xs text-default-400 italic">No inspections found.</p>;
	}
	return (
		<Table aria-label="Inspections" removeWrapper isStriped>
			<TableHeader>
				<TableColumn>NAME</TableColumn>
				<TableColumn align="end" className="text-right">
					DUE DATE/METER
				</TableColumn>
				<TableColumn align="end" className="text-right">
					TIME LEFT
				</TableColumn>
			</TableHeader>
			<TableBody items={inspections}>
				{ins => {
					const name = ins.inspectionName || ins.name || '—';
					// L: show dueDate OR counterExpirationValue, whichever is present
					const dueDisplay = ins.dueDate
						? formatDateValue(ins.dueDate, userSettings, timezone)
						: ins.counterExpirationValue != null
							? String(ins.counterExpirationValue)
							: '—';
					// M: meter-based → dueIn is a raw number, append " hours"; date-based → verbatim string
					const isMeterBased = ins.counterExpirationValue != null;
					const timeLeft =
						ins.dueIn != null
							? isMeterBased && /^\d+(\.\d+)?$/.test(String(ins.dueIn))
								? `${ins.dueIn} hours`
								: String(ins.dueIn)
							: '—';
					// N: row coloring — danger=red, warning=orange
					const isDanger = ins.danger === '1' || ins.danger === true;
					const isWarning = ins.warning === '1' || ins.warning === true;
					const rowClass = isDanger ? 'text-danger' : isWarning ? 'text-warning' : '';
					return (
						<TableRow key={ins.id ?? name} className={rowClass}>
							<TableCell className="font-medium">{name}</TableCell>
							<TableCell className="text-right tabular-nums">{dueDisplay}</TableCell>
							<TableCell className="text-right tabular-nums">{timeLeft}</TableCell>
						</TableRow>
					);
				}}
			</TableBody>
		</Table>
	);
}

// ─── Discrepancies section ────────────────────────────────────────────────────
// Canonical implementation extracted to `./DiscrepanciesSection.js` and shared
// with CheckInScreen (epic e5cea696). Imported above.

// ─── Section title ────────────────────────────────────────────────────────────

function SectionTitle({ children }) {
	return (
		<p className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-2">
			{children}
		</p>
	);
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * ResourcePanel — read-only resource subpanel inside EventViewModal.
 *
 * Props:
 *   resource          { id, type: 'aircraft'|'simulator', label }
 *   eventId           event ID (for counter-history fetch)
 *   organizationId    numeric org ID (for counter-history fetch)
 *   orgUnique         event's org unique slug (cross-org routing — R)
 *   eventTimezone     IANA tz for inspections query
 *   hasCheckOut       bool, derived from `event.checkOutTime != null`. Gates
 *                     the `getEventHistory` fetch — Not Started events
 *                     (`checkOutTime == null`) read from `fallbackCounters`
 *                     (aircraft current reading, no oldValue/diff) so a Start
 *                     edit + reload doesn't leak a `(start, end, total)`
 *                     triple. Checked Out / Completed (Checked In) both have
 *                     `checkOutTime != null` and need the history records to
 *                     populate End + Total. Legacy parity
 *                     (`calendar.event-resource.js:75-81`).
 *   hasCheckIn        bool, derived from `Number(event.checkInTime) > 0`. M11
 *                     (Rem38): distinguishes Checked In (Start + End + Total)
 *                     from Checked Out (Start only). Feeds `normalizeCounter`'s
 *                     event-state branching so a Pencil edit at Check Out
 *                     doesn't render the history `(oldValue, value, diff)` as a
 *                     full triple before the event is Checked In. See
 *                     `project-metersection-edit-spec`.
 *   onBack            return to previous screen
 *   onDiscrepancyClick  (discrepancy) => void — drill into discrepancy screen
 *   perms             from getEventOrgPerms (per-event org)
 *   currentUserId     for pilot bypass on Meters
 *   pilotId           event.pilotId for pilot bypass
 *   userSettings      Redux state.calendarMonthBootstrap.userSettings
 *   eventTypeLabel    e.g. "Regular Event" for breadcrumb
 */
function ResourcePanel({
	resource,
	eventId,
	organizationId,
	orgUnique,
	eventTimezone,
	hasCheckOut,
	hasCheckIn,
	onBack,
	onDiscrepancyClick,
	perms,
	currentUserId,
	pilotId,
	userSettings,
	eventTypeLabel
}) {
	const [loading, setLoading] = useState(true);
	const [resourceData, setResourceData] = useState(null);
	const [inspections, setInspections] = useState(null);
	const [discrepancies, setDiscrepancies] = useState(null);
	const [counters, setCounters] = useState([]);
	const [showDetails, setShowDetails] = useState(false);
	const [error, setError] = useState(null);
	const [discrepancyRefetchKey, setDiscrepancyRefetchKey] = useState(0);
	const [mechanics, setMechanics] = useState([]);
	const [addDiscrepancyOpen, setAddDiscrepancyOpen] = useState(false);

	const isAircraft = resource?.type === 'aircraft';
	const pilotIsMe =
		pilotId != null && currentUserId != null && String(pilotId) === String(currentUserId);
	const canSeeMeters = !!(perms?.canViewMeters || pilotIsMe);
	// Legacy `calendar.event-resource.js:242` — `userIsAPilot || counterEdit`.
	// Pilot bypass folded in here (mirrors `canSeeMeters`).
	const canEditCounter = !!(perms?.canEditCounter || pilotIsMe);

	useEffect(() => {
		if (!resource?.id) return;
		let cancelled = false;
		setLoading(true);
		setError(null);
		setResourceData(null);
		setInspections(null);
		setDiscrepancies(null);
		setCounters([]);

		const fetchResource = isAircraft
			? ResourcesApi.getAircraft(resource.id, orgUnique)
			: ResourcesApi.getSimulator(resource.id, orgUnique);

		const fetchInspections =
			isAircraft && perms?.canViewInspections
				? ResourcesApi.getInspections(resource.id, eventTimezone || 'UTC', orgUnique)
				: Promise.resolve(null);

		const fetchDiscrepancies =
			isAircraft && perms?.canViewDiscrepancies
				? DiscrepanciesApi.getAircraftDiscrepancies(resource.id, orgUnique)
				: Promise.resolve(null);

		// Legacy parity (`calendar.event-resource.js:75-81` — `getCounterSource`):
		// only events that have been checked out carry history-bearing counters.
		// We use `event.checkOutTime != null` as the signal — it stays truthy
		// across Checked Out AND Completed (Checked In) states, where the enable
		// flags both flip to 0. Not Started (`checkOutTime == null`) reads from
		// `resourceData.counters` (the existing `fallbackCounters`) via
		// `MetersSection`'s `counters.length ? counters : fallbackCounters` —
		// prevents Start-edit + reload from leaking `(start, end, total)`.
		const hasEventStateHistory = hasCheckOut;
		const fetchCounters =
			canSeeMeters &&
			organizationId != null &&
			eventId != null &&
			hasEventStateHistory
				? CounterApi.getEventHistory(eventId, organizationId)
				: Promise.resolve(null);

		// S: Promise.allSettled — individual fetch failures don't kill the whole panel
		Promise.allSettled([fetchResource, fetchInspections, fetchDiscrepancies, fetchCounters])
			.then(([resourceRes, insRes, discRes, cntRes]) => {
				if (cancelled) return;

				// Resource is critical — panel error if it fails
				if (resourceRes.status === 'rejected') {
					const err = resourceRes.reason;
					setError(err?.response?.data?.error?.[0] || err?.message || 'Failed to load resource');
					return;
				}
				const rawResourceData = unwrap(resourceRes.value);
				setResourceData(rawResourceData);

				// Inspections — graceful degrade to empty on failure
				if (insRes.status === 'fulfilled' && insRes.value != null) {
					setInspections(unwrap(insRes.value));
				} else {
					setInspections(null);
				}

				// Discrepancies — graceful degrade to empty on failure
				if (discRes.status === 'fulfilled' && discRes.value != null) {
					setDiscrepancies(unwrap(discRes.value));
				} else {
					setDiscrepancies(null);
				}

				// R6.3: Counter-history — filter to this resource's own counters
				if (cntRes.status === 'fulfilled' && cntRes.value != null) {
					const raw = unwrap(cntRes.value);
					const rawList = Array.isArray(raw)
						? raw
						: Array.isArray(raw?.counters)
							? raw.counters
							: Array.isArray(raw?.data)
								? raw.data
								: [];
					const ownCounterIds = new Set((rawResourceData?.counters || []).map(c => String(c.id)));
					const scoped =
						ownCounterIds.size > 0
							? rawList.filter(row => {
									const cid = row.counterId ?? row.counter?.id ?? row.id;
									return ownCounterIds.has(String(cid));
								})
							: []; // fail-closed: no own counters known → don't leak cross-resource history
					// M11-dedup (Rem38) — `getEventHistory` can return MULTIPLE rows
					// for the same counter (e.g. a Pencil `editByAdmin` row + the
					// `checkIn` row), which rendered as duplicate MetersSection rows
					// + a React "two children with the same key" warning. Keep only
					// the LATEST row per counterId (largest `dateOfChange`).
					const byCounterId = new Map();
					scoped.forEach(row => {
						const cid = String(row.counterId ?? row.counter?.id ?? row.id);
						const existing = byCounterId.get(cid);
						if (!existing || Number(row.dateOfChange ?? 0) > Number(existing.dateOfChange ?? 0)) {
							byCounterId.set(cid, row);
						}
					});
					setCounters(
						Array.from(byCounterId.values()).map(c =>
							normalizeCounter(c, { hasCheckOut, hasCheckIn })
						)
					);
				} else {
					setCounters([]); // fallbackCounters from resourceData used instead
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [
		resource?.id,
		resource?.type,
		eventId,
		organizationId,
		orgUnique,
		eventTimezone,
		isAircraft,
		canSeeMeters,
		hasCheckOut,
		hasCheckIn,
		perms?.canViewInspections,
		perms?.canViewDiscrepancies
	]);

	// Re-fetch just the discrepancy list after an inline-edit save.
	// Fires only when discrepancyRefetchKey increments (> 0); the main
	// useEffect above handles the initial load.
	useEffect(() => {
		if (discrepancyRefetchKey === 0 || !resource?.id || !isAircraft || !perms?.canViewDiscrepancies)
			return;
		let cancelled = false;
		DiscrepanciesApi.getAircraftDiscrepancies(resource.id, orgUnique)
			.then(res => {
				if (!cancelled) setDiscrepancies(unwrap(res));
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [discrepancyRefetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleDiscrepancyUpdated = useCallback(
		() => setDiscrepancyRefetchKey(k => k + 1),
		[]
	);

	// Fetch mechanics for inline-edit assignTo + DiscrepancyAddScreen.
	// Gate on either manage or add permission so the list is available whenever
	// the user can interact with the discrepancy section.
	useEffect(() => {
		if (!isAircraft || !orgUnique) return;
		if (!perms?.canManageDiscrepancies && !perms?.canAddDiscrepancy) return;
		let cancelled = false;
		DiscrepanciesApi.getMechanics(orgUnique)
			.then(res => {
				if (!cancelled) {
					const list = unwrap(res);
					setMechanics(Array.isArray(list) ? list : []);
				}
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [isAircraft, orgUnique, perms?.canManageDiscrepancies, perms?.canAddDiscrepancy]);

	const handleAddDiscrepancyClick = useCallback(() => setAddDiscrepancyOpen(true), []);

	const handleAddDiscrepancyBack = useCallback(
		refresh => {
			setAddDiscrepancyOpen(false);
			if (refresh) handleDiscrepancyUpdated();
		},
		[handleDiscrepancyUpdated]
	);

	// Fallback: current counters from aircraft/simulator payload
	const fallbackCounters = (() => {
		if (!resourceData) return [];
		const raw = Array.isArray(resourceData.counters) ? resourceData.counters : [];
		return raw.map(c => normalizeCounter(c, { hasCheckOut, hasCheckIn }));
	})();

	const title = resource?.label || (isAircraft ? 'Aircraft' : 'Simulator');
	const typeLabel = isAircraft ? 'Aircraft' : 'Simulator';

	// ─── Breadcrumb (inside header) ──────────────────────────────────────────
	const Breadcrumb = (
		<Breadcrumbs size="sm" className="mb-1">
			<BreadcrumbItem onPress={onBack}>{eventTypeLabel || 'Event'}</BreadcrumbItem>
			<BreadcrumbItem isCurrent>View Resource</BreadcrumbItem>
		</Breadcrumbs>
	);

	if (loading) {
		return (
			<>
				<ModalHeader className="flex flex-col border-b border-default-200 pr-10">
					{Breadcrumb}
					<div className="flex items-center gap-3">
						<Button isIconOnly size="sm" variant="light" onPress={onBack}>
							<ChevronLeft className="w-4 h-4" />
						</Button>
						<span className="text-lg font-bold" style={{ color: 'hsl(var(--heroui-foreground))' }}>
							{typeLabel}: {title}
						</span>
					</div>
				</ModalHeader>
				<ModalBody className="flex items-center justify-center py-16">
					<Spinner size="lg" />
				</ModalBody>
			</>
		);
	}

	if (error) {
		return (
			<>
				<ModalHeader className="flex flex-col border-b border-default-200 pr-10">
					{Breadcrumb}
					<div className="flex items-center gap-3">
						<Button isIconOnly size="sm" variant="light" onPress={onBack}>
							<ChevronLeft className="w-4 h-4" />
						</Button>
						<span className="text-lg font-bold" style={{ color: 'hsl(var(--heroui-foreground))' }}>
							{typeLabel}: {title}
						</span>
					</div>
				</ModalHeader>
				<ModalBody>
					<p className="text-danger text-sm">{error}</p>
				</ModalBody>
				<ModalFooter>
					<Button variant="light" onPress={onBack}>
						Back
					</Button>
				</ModalFooter>
			</>
		);
	}

	// Add Discrepancy sub-screen — replaces main panel body (CheckInScreen pattern)
	if (addDiscrepancyOpen) {
		return (
			<DiscrepancyAddScreen
				aircraftId={resource?.type === 'aircraft' ? resource.id : null}
				orgUnique={orgUnique}
				mechanics={mechanics}
				onBack={handleAddDiscrepancyBack}
			/>
		);
	}

	return (
		<>
			<ModalHeader className="flex flex-col border-b border-default-200 pr-10">
				{Breadcrumb}
				<div className="flex items-center gap-3">
					<Button isIconOnly size="sm" variant="light" onPress={onBack} aria-label="Back">
						<ChevronLeft className="w-4 h-4" />
					</Button>
					<span
						className="text-lg font-bold flex-1 flex items-center gap-2"
						style={{ color: 'hsl(var(--heroui-foreground))' }}
					>
						{typeLabel}: {title}
						{resource?.type === 'aircraft' &&
							Array.isArray(inspections) &&
							inspections.length > 0 &&
							(getAirworthinessStatusFromInspections(inspections) === 'unairworthy' ? (
								<XCircle
									className="w-5 h-5 text-danger"
									fill="currentColor"
									stroke="white"
									strokeWidth={2.5}
								/>
							) : (
								<CheckCircle
									className="w-5 h-5 text-success"
									fill="currentColor"
									stroke="white"
									strokeWidth={2.5}
								/>
							))}
					</span>
				</div>
			</ModalHeader>

			<ModalBody className="pt-4 space-y-5">
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
				>
					{/* Show Details toggle */}
					<div className="mb-4">
						<button
							onClick={() => setShowDetails(v => !v)}
							className="flex items-center gap-1 text-sm font-medium hover:underline"
							style={{ color: 'hsl(var(--heroui-primary))' }}
						>
							{showDetails ? (
								<ChevronUp className="w-4 h-4" />
							) : (
								<ChevronDown className="w-4 h-4" />
							)}
							{showDetails ? 'Hide Details' : 'Show Details'}
						</button>
						<AnimatePresence initial={false}>
							{showDetails && (
								<motion.div
									key="details"
									initial={{ height: 0, opacity: 0 }}
									animate={{ height: 'auto', opacity: 1 }}
									exit={{ height: 0, opacity: 0 }}
									transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
									style={{ overflow: 'hidden' }}
								>
									<ShowDetailsPanel resourceData={resourceData} isAircraft={isAircraft} />
								</motion.div>
							)}
						</AnimatePresence>
					</div>

					{/* Meters / Counters */}
					{canSeeMeters && (
						<div>
							<SectionTitle>Meter Values</SectionTitle>
							<MetersSection
								counters={counters}
								fallbackCounters={fallbackCounters}
								canEditCounter={canEditCounter}
								orgUnique={orgUnique}
								eventId={eventId}
								organizationId={organizationId}
								timezone={eventTimezone}
								resourceId={resource?.id}
								isSimulator={resource?.type === 'simulator'}
							/>
						</div>
					)}

					{/* Inspections — aircraft only */}
					{isAircraft && perms?.canViewInspections && (
						<>
							<Divider />
							<div>
								<SectionTitle>Inspections</SectionTitle>
								<InspectionsSection
									inspections={inspections}
									userSettings={userSettings}
									timezone={eventTimezone}
								/>
							</div>
						</>
					)}

					{/* Discrepancies — aircraft only */}
					{isAircraft && perms?.canViewDiscrepancies && (
						<>
							<Divider />
							<div>
								<div className="flex items-center justify-between mb-2">
									<p className="text-xs font-semibold text-default-500 uppercase tracking-wide">
										Discrepancies
									</p>
									{!!perms?.canAddDiscrepancy && (
										<Button
											size="sm"
											color="success"
											variant="flat"
											onPress={handleAddDiscrepancyClick}
										>
											+ Add
										</Button>
									)}
								</div>
								<DiscrepanciesSection
									discrepancies={discrepancies}
									userSettings={userSettings}
									onDiscrepancyClick={onDiscrepancyClick}
									canViewDiscrepancies={perms?.canViewDiscrepancies}
									canChangeDiscrepancyStatus={!!perms?.canChangeDiscrepancyStatus}
									canManageDiscrepancies={!!perms?.canManageDiscrepancies}
									orgUnique={orgUnique}
									onDiscrepancyUpdated={handleDiscrepancyUpdated}
									mechanics={mechanics}
								/>
							</div>
						</>
					)}
				</motion.div>
			</ModalBody>

			<ModalFooter className="border-t border-default-200 pt-3">
				<Button variant="light" onPress={onBack}>
					Back
				</Button>
			</ModalFooter>
		</>
	);
}

export default ResourcePanel;

// ─── Local field helper ───────────────────────────────────────────────────────

function DiscField({ label, children }) {
	return (
		<div className="mb-3">
			<p className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-0.5">
				{label}
			</p>
			<div className="text-sm text-default-900">{children}</div>
		</div>
	);
}

// ─── Discrepancy detail screen ────────────────────────────────────────────────

/**
 * Read-only discrepancy detail screen rendered inside EventViewModal when the
 * user clicks a discrepancy row in ResourcePanel. Shows 6 fields + description.
 *
 * Props:
 *   discrepancy     discrepancy object from DiscrepanciesApi
 *   onBack          go back to resource screen
 *   onBackToEvent   go back to event view screen (breadcrumb "Event" press)
 *   userSettings    from Redux (calendarMonthBootstrap.userSettings)
 *   eventTypeLabel  e.g. "Regular Event"
 *   resourceLabel   e.g. "N12345" (aircraft reg or simulator name)
 */
export function DiscrepancyScreen({
	discrepancy,
	onBack,
	onBackToEvent,
	userSettings,
	eventTypeLabel,
	resourceLabel
}) {
	// Display tz for the report date (same selector EventViewModal uses).
	const timezone = useSelector(
		state => pickCalendarFilters(state)?.timezone || pickCalendarBootstrap(state)?.timezone || 'UTC'
	);
	const d = discrepancy || {};
	const statusCfg = DISC_STATUS_CONFIG[d.status] || { label: d.status || '—', color: 'default' };
	const priorityLabel = DISC_PRIORITY[d.priority] || String(d.priority || '—');
	const date = d.date || d.createdAt || d.created_at || null;
	const reportedDate = date ? formatDateValue(date, userSettings, timezone) : '—';
	const assignedTo =
		[d.assignedName, d.assignedSurname].filter(Boolean).join(' ') ||
		d.assignedTo ||
		d.assignee ||
		'—';
	const reportedBy =
		[d.pilotName, d.pilotSurname].filter(Boolean).join(' ') || d.reportedBy || d.reporter || '—';
	const description = d.description || d.comments || '';

	return (
		<>
			<ModalHeader className="flex flex-col border-b border-default-200 pr-10">
				<Breadcrumbs size="sm" className="mb-1">
					<BreadcrumbItem onPress={onBackToEvent}>{eventTypeLabel || 'Event'}</BreadcrumbItem>
					<BreadcrumbItem onPress={onBack}>{resourceLabel || 'Resource'}</BreadcrumbItem>
					<BreadcrumbItem isCurrent>Discrepancy</BreadcrumbItem>
				</Breadcrumbs>
				<div className="flex items-center gap-3">
					<Button isIconOnly size="sm" variant="light" onPress={onBack} aria-label="Back">
						<ChevronLeft className="w-4 h-4" />
					</Button>
					<span
						className="text-lg font-bold flex-1"
						style={{ color: 'hsl(var(--heroui-foreground))' }}
					>
						{d.name || 'Discrepancy'}
					</span>
				</div>
			</ModalHeader>
			<ModalBody className="pt-5">
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
					className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0"
				>
					<div>
						<DiscField label="Name">{d.name || '—'}</DiscField>
						<DiscField label="Status">
							<Chip size="sm" variant="flat" color={statusCfg.color}>
								{statusCfg.label}
							</Chip>
						</DiscField>
						<DiscField label="Priority">{priorityLabel}</DiscField>
					</div>
					<div>
						<DiscField label="Assigned To">{assignedTo}</DiscField>
						<DiscField label="Report By">{reportedBy}</DiscField>
						<DiscField label="Report Date">{reportedDate}</DiscField>
					</div>
				</motion.div>
				{description ? (
					<div className="mt-4">
						<p className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-0.5">
							Description
						</p>
						<p className="text-sm text-default-900 whitespace-pre-wrap">{description}</p>
					</div>
				) : null}
			</ModalBody>
			<ModalFooter className="border-t border-default-200 pt-3">
				<Button variant="light" onPress={onBack}>
					Back
				</Button>
			</ModalFooter>
		</>
	);
}
